import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Grid, Card, CardContent, Dialog, DialogTitle, 
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, 
  List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton, TextField, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment, Grow, Stack, Divider,
  Tabs, Tab, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import API_URL from '../config';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      if (setPosition) {
          setPosition(e.latlng);
          map.flyTo(e.latlng, map.getZoom());
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function Punching() {
  const [locations, setLocations] = useState([]);
  const [punchLocations, setPunchLocations] = useState([]); // reusable locations
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPunchLocation, setSelectedPunchLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({ lat: 25.0, lng: 45.0 }); // Default
  const [address, setAddress] = useState('');
  const [userPunchingId, setUserPunchingId] = useState('');
  const [assignMode, setAssignMode] = useState('select'); // 'select' = pick existing, 'custom' = drop pin

  // --- Manage Locations state ---
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTab, setManageTab] = useState(0); // 0 = list, 1 = add/edit
  const [editingPunchLoc, setEditingPunchLoc] = useState(null);
  const [punchLocForm, setPunchLocForm] = useState({
    name: '',
    latitude: 25.0,
    longitude: 45.0,
    address: ''
  });

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [locRes, punchRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/punching/locations`, { headers }),
        axios.get(`${API_URL}/punching/punch-locations`, { headers }),
        axios.get(`${API_URL}/punching/users`, { headers })
      ]);
      setLocations(locRes.data);
      setPunchLocations(punchRes.data);
      setUsers(userRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- Assignment save ---
  const handleSave = async () => {
    if (!selectedUser) return alert('Select a user');
    setLoading(true);
    
    const payload = {
      user_id: selectedUser.id,
      userpunchingid: userPunchingId
    };

    if (assignMode === 'select' && selectedPunchLocation) {
      payload.location_id = selectedPunchLocation.id;
      // address is optional — will be inherited
      payload.address = address;
    } else {
      payload.latitude = newLocation.lat;
      payload.longitude = newLocation.lng;
      payload.address = address;
    }

    try {
        await axios.post(`${API_URL}/punching/locations`, payload, { headers });
        setOpen(false);
        fetchData();
        setSelectedUser(null);
        setSelectedPunchLocation(null);
        setAddress('');
        setUserPunchingId('');
        setAssignMode('select');
    } catch (err) {
        alert('Error saving location');
    } finally {
        setLoading(false);
    }
  };

  const handleDelete = async (id) => {
      if(!window.confirm('Delete this location assignment?')) return;
      try {
          await axios.delete(`${API_URL}/punching/locations/${id}`, { headers });
          fetchData();
      } catch (err) {
          alert('Error deleting');
      }
  };

  const handleUserClick = (userId) => {
      navigate(`/punching/${userId}`);
  };

  // --- Punch Location management ---
  const handleOpenManage = () => {
    setManageTab(0);
    setEditingPunchLoc(null);
    setPunchLocForm({ name: '', latitude: 25.0, longitude: 45.0, address: '' });
    setManageOpen(true);
  };

  const handleEditPunchLoc = (ploc) => {
    setEditingPunchLoc(ploc);
    setPunchLocForm({
      name: ploc.name,
      latitude: ploc.latitude,
      longitude: ploc.longitude,
      address: ploc.address || ''
    });
    setManageTab(1);
  };

  const handleSavePunchLocation = async () => {
    const { name, latitude, longitude, address } = punchLocForm;
    if (!name.trim()) return alert('Location name is required');

    try {
      if (editingPunchLoc) {
        await axios.put(`${API_URL}/punching/punch-locations/${editingPunchLoc.id}`, {
          name, latitude, longitude, address
        }, { headers });
      } else {
        await axios.post(`${API_URL}/punching/punch-locations`, {
          name, latitude, longitude, address
        }, { headers });
      }
      setEditingPunchLoc(null);
      setPunchLocForm({ name: '', latitude: 25.0, longitude: 45.0, address: '' });
      fetchData();
      setManageTab(0);
    } catch (err) {
      alert('Error saving punch location');
    }
  };

  const handleDeletePunchLocation = async (id) => {
    if (!window.confirm('Delete this location? Assigned users will keep their assignment but lose the location reference.')) return;
    try {
      await axios.delete(`${API_URL}/punching/punch-locations/${id}`, { headers });
      fetchData();
    } catch (err) {
      alert('Error deleting');
    }
  };

  const filteredLocations = locations.filter(loc => 
      (loc.user_name && loc.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (loc.address && loc.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (loc.punch_location_name && loc.punch_location_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <LocationOnIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    Punching Assignments
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Manage and track work locations and assignments
                </Typography>
            </Box>
            
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', gap: 2 }}>
              <Button 
                  variant="outlined"
                  onClick={handleOpenManage}
                  sx={{ 
                      borderRadius: 2, fontWeight: 'bold', px: 3, py: 1,
                      borderColor: 'rgba(255,255,255,0.4)', color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'white' }
                  }}
              >
                  Manage Locations
              </Button>
              <Button 
                  variant="contained" 
                  startIcon={<AddIcon />} 
                  onClick={() => {
                    setAssignMode('select');
                    setSelectedPunchLocation(null);
                    setAddress('');
                    setOpen(true);
                  }}
                  sx={{ 
                      borderRadius: 2, fontWeight: 'bold', px: 3, py: 1,
                      bgcolor: 'white', color: '#1e3c72',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                  }}
              >
                  New Assignment
              </Button>
            </Box>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Filters and Controls */}
      <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center' }, gap: 2 }}>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                <TextField 
                    placeholder="Search User or Location..." 
                    size="small" 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                    }}
                    sx={{ 
                        flexGrow: 1, 
                        minWidth: { sm: 250 },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#e2e8f0' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                        }
                    }}
                />
            </Box>
        </Box>
      </Card>

      {filteredLocations.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
              <Typography color="text.secondary">No users assigned to locations yet or no matches found.</Typography>
          </Paper>
      ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', overflowX: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>Punching ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>Location</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }}>Coordinates</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#475569', py: 2 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredLocations.map((loc) => (
                            <TableRow 
                                key={loc.id} 
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: '#f1f5f9' }, '&:last-child td, &:last-child th': { border: 0 } }}
                                onClick={() => handleUserClick(loc.user_id)}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                            {loc.user_name ? loc.user_name.charAt(0) : '?'}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight="600" color="#1e293b">{loc.user_name}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1, color: '#1e3c72', fontWeight: 'bold', display: 'inline-block' }}>
                                        {loc.userpunchingid || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationOnIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                        <Box>
                                          {loc.punch_location_name && (
                                            <Chip label={loc.punch_location_name} size="small" color="primary" sx={{ mr: 1, fontSize: '0.7rem', height: 20 }} />
                                          )}
                                          <Typography variant="body2" color="text.secondary" fontWeight="500" component="span">
                                            {loc.address || 'No Address'}
                                          </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#f8fafc', px: 1, py: 0.5, borderRadius: 1, color: '#475569', fontWeight: 'bold' }}>
                                        {loc.latitude?.toFixed(6) || '—'}, {loc.longitude?.toFixed(6) || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Button 
                                        size="small" 
                                        variant="outlined"
                                        onClick={(e) => { e.stopPropagation(); handleUserClick(loc.user_id); }}
                                        sx={{ mr: 1, borderRadius: 2, fontWeight: 'bold', minWidth: '80px', '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6' } }}
                                    >
                                        Details
                                    </Button>
                                    <IconButton 
                                        size="small" 
                                        sx={{ color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                                        onClick={(e) => { e.stopPropagation(); handleDelete(loc.id); }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
          </TableContainer>
      )}

      {/* Add Assignment Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
            sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }
        }}
      >
        <DialogTitle sx={{ 
            fontWeight: '800', 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 3
        }}>
            <LocationOnIcon /> Assign Work Location
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>USER</Typography>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => <TextField {...params} label="Search User" fullWidth sx={{ bgcolor: 'white' }} />}
                        />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>PUNCHING ID</Typography>
                        <TextField
                            label="User Punching ID"
                            fullWidth
                            value={userPunchingId}
                            onChange={e => setUserPunchingId(e.target.value)}
                            placeholder="e.g. EMP-001"
                            sx={{ bgcolor: 'white', mb: 2 }}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>LOCATION MODE</Typography>
                        <Tabs
                          value={assignMode}
                          onChange={(e, v) => { setAssignMode(v); setSelectedPunchLocation(null); }}
                          sx={{ mb: 2, minHeight: 'auto', '& .MuiTab-root': { minHeight: 'auto', py: 1, px: 1.5, fontSize: '0.75rem', fontWeight: 'bold' } }}
                        >
                          <Tab label="Saved Location" value="select" />
                          <Tab label="Custom Pin" value="custom" />
                        </Tabs>

                        {assignMode === 'select' ? (
                          <Autocomplete
                              options={punchLocations}
                              getOptionLabel={(opt) => `${opt.name} (${opt.latitude?.toFixed(4)}, ${opt.longitude?.toFixed(4)})`}
                              value={selectedPunchLocation}
                              onChange={(e, v) => setSelectedPunchLocation(v)}
                              renderInput={(params) => <TextField {...params} label="Select Location" fullWidth sx={{ bgcolor: 'white' }} />}
                          />
                        ) : (
                          <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
                              <Typography variant="caption" color="#2563eb" fontWeight="bold" display="block">SELECTED COORDINATES</Typography>
                              <Typography variant="body2" fontWeight="800" sx={{ mt: 0.5, color: '#1e3a8a' }}>{newLocation.lat.toFixed(6)}, {newLocation.lng.toFixed(6)}</Typography>
                              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#3b82f6' }}>
                                  Click on the map to drop a pin.
                              </Typography>
                          </Box>
                        )}
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>ADDRESS (optional)</Typography>
                        <TextField 
                            fullWidth 
                            label="Address / notes" 
                            value={address} 
                            onChange={e => setAddress(e.target.value)}
                            placeholder={assignMode === 'select' && selectedPunchLocation ? `Inherited from ${selectedPunchLocation.name}` : 'e.g. Main Office, Site A'}
                            sx={{ bgcolor: 'white' }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>MAP {assignMode === 'select' && selectedPunchLocation ? '(preview)' : 'PIN'}</Typography>
                    <Box sx={{ height: 400, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <MapContainer 
                          center={
                            assignMode === 'select' && selectedPunchLocation 
                              ? [selectedPunchLocation.latitude, selectedPunchLocation.longitude] 
                              : [25, 45]
                          } 
                          zoom={assignMode === 'select' && selectedPunchLocation ? 15 : 4} 
                          style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            {assignMode === 'select' && selectedPunchLocation ? (
                              <Marker position={[selectedPunchLocation.latitude, selectedPunchLocation.longitude]}>
                                <Popup>{selectedPunchLocation.name}</Popup>
                              </Marker>
                            ) : (
                              <LocationMarker position={newLocation} setPosition={setNewLocation} />
                            )}
                        </MapContainer>
                    </Box>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
            <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}>
                {loading ? 'Saving...' : 'Save Assignment'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Locations Dialog */}
      <Dialog 
        open={manageOpen} 
        onClose={() => setManageOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' } }}
      >
        <DialogTitle sx={{ 
            fontWeight: '800', 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 3
        }}>
            <LocationOnIcon /> Manage Saved Locations
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
          <Tabs value={manageTab} onChange={(e, v) => setManageTab(v)} sx={{ mb: 2 }}>
            <Tab label="Locations List" />
            <Tab label={editingPunchLoc ? 'Edit Location' : 'Add Location'} />
          </Tabs>

          {manageTab === 0 && (
            <>
              {punchLocations.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 2 }}>
                  <Typography color="text.secondary">No saved locations yet. Switch to Add tab to create one.</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #f1f5f9' }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Coordinates</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {punchLocations.map(pl => (
                        <TableRow key={pl.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{pl.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {pl.latitude?.toFixed(6)}, {pl.longitude?.toFixed(6)}
                          </TableCell>
                          <TableCell>{pl.address || '—'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEditPunchLoc(pl)} sx={{ color: '#3b82f6' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeletePunchLocation(pl.id)} sx={{ color: '#ef4444' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {manageTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Location Name *"
                  fullWidth
                  value={punchLocForm.name}
                  onChange={e => setPunchLocForm({...punchLocForm, name: e.target.value})}
                  placeholder="e.g. Main Office, Site A"
                  sx={{ bgcolor: 'white', mb: 2 }}
                />
                <TextField
                  label="Address"
                  fullWidth
                  value={punchLocForm.address}
                  onChange={e => setPunchLocForm({...punchLocForm, address: e.target.value})}
                  sx={{ bgcolor: 'white', mb: 2 }}
                />
                <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe', mb: 2 }}>
                  <Typography variant="caption" color="#2563eb" fontWeight="bold" display="block">COORDINATES</Typography>
                  <TextField
                    label="Latitude"
                    type="number"
                    fullWidth
                    value={punchLocForm.latitude}
                    onChange={e => setPunchLocForm({...punchLocForm, latitude: parseFloat(e.target.value) || 0})}
                    size="small"
                    sx={{ bgcolor: 'white', mt: 1 }}
                    inputProps={{ step: 0.000001 }}
                  />
                  <TextField
                    label="Longitude"
                    type="number"
                    fullWidth
                    value={punchLocForm.longitude}
                    onChange={e => setPunchLocForm({...punchLocForm, longitude: parseFloat(e.target.value) || 0})}
                    size="small"
                    sx={{ bgcolor: 'white', mt: 1 }}
                    inputProps={{ step: 0.000001 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MyLocationIcon />}
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (pos) => {
                            setPunchLocForm(prev => ({
                              ...prev,
                              latitude: pos.coords.latitude,
                              longitude: pos.coords.longitude
                            }));
                          },
                          () => alert('Unable to retrieve your location. Please allow location access in your browser.'),
                          { enableHighAccuracy: true, timeout: 10000 }
                        );
                      } else {
                        alert('Geolocation is not supported by your browser.');
                      }
                    }}
                    sx={{ mt: 1, borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                  >
                    Use My Current Location
                  </Button>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#3b82f6' }}>
                    Click on the map to set coordinates, or type them directly.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" onClick={handleSavePunchLocation} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
                    {editingPunchLoc ? 'Update' : 'Save'}
                  </Button>
                  <Button onClick={() => { setManageTab(0); setEditingPunchLoc(null); }} sx={{ color: 'text.secondary' }}>
                    Cancel
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>MAP PIN</Typography>
                <Box sx={{ height: 400, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <MapContainer center={[punchLocForm.latitude, punchLocForm.longitude]} zoom={10} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <LocationMarker 
                      position={{ lat: punchLocForm.latitude, lng: punchLocForm.longitude }} 
                      setPosition={(pos) => setPunchLocForm(prev => ({ ...prev, latitude: pos.lat, longitude: pos.lng }))} 
                    />
                  </MapContainer>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button onClick={() => setManageOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Punching;